import { useEffect, useRef, useState } from 'react'
import { SearchableSelect, Button, LoadingSpinner } from '../../components/BaseComponents'
import CanonicalModal from '../../components/BaseComponents/CanonicalModal/CanonicalModal'
import Label from '../../components/BaseComponents/Label'
import useApi from '../../hooks/useApi'
import useCleanup from '../../hooks/useCleanup'
import { getApiErrorMessage, getCSRFToken } from '../../lib/utils'
import { SelectDropdown } from '../../types/SelectDropdown'

export type AttachableUser = {
	id: string
	fullName: string
	email: string
}

type AddToOrganisationModalProps = {
	user: AttachableUser | null
	onClose: () => void
	onAdded: () => void
}

// B-6a: lets an admin put a registered user (typically one with no organisation) into any organisation.
function AddToOrganisationModal({ user, onClose, onAdded }: AddToOrganisationModalProps) {
	const api = useApi()
	const [companyOptions, setCompanyOptions] = useState<Array<SelectDropdown>>([])
	const [roleOptions, setRoleOptions] = useState<Array<SelectDropdown>>([])
	const [companyId, setCompanyId] = useState('')
	const [roleId, setRoleId] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [lookupError, setLookupError] = useState('')
	const [loadingOptions, setLoadingOptions] = useState(false)
	const [loadedUserId, setLoadedUserId] = useState<string>()
	const [loadedApi, setLoadedApi] = useState<ReturnType<typeof useApi>>()
	const context = useRef({ userId: user?.id, api, generation: 0 })
	if (context.current.userId !== user?.id || context.current.api !== api) {
		context.current = { userId: user?.id, api, generation: context.current.generation + 1 }
	}
	useEffect(() => () => { context.current.generation += 1 }, [])

	const loadOptions = useCleanup(() => {
		let cancelled = false
		setLoadedUserId(user?.id)
		setLoadedApi(api)
		setCompanyOptions([])
		setRoleOptions([])
		setCompanyId('')
		setRoleId('')
		setError(null)
		setLookupError('')
		setSubmitting(false)
		if (!user) {
			setLoadingOptions(false)
			return () => { cancelled = true }
		}
		setLoadingOptions(true)
		const run = async () => {
			try {
				const [companies, roles] = await Promise.all([
					api.get(`company?${new URLSearchParams({ page: '1', limit: '1000', orderBy: 'name', direction: 'asc' })}`),
					api.get('roles'),
				])
				if (cancelled) {
					return
				}
				setCompanyOptions((companies.companies?.data ?? []).map((c: any) => ({ label: c.name, value: c.id })))
				const options: Array<SelectDropdown> = (roles.data?.roles ?? [])
					.filter((role: any) => role.name !== 'admin')
					.map((role: any) => ({
						label: role.name.charAt(0).toUpperCase() + role.name.slice(1),
						value: role.id,
					}))
				setRoleOptions(options)
				setRoleId(options.find((option) => option.label === 'Company')?.value ?? '')
			} catch (_) {
				if (!cancelled) {
					setLookupError('Failed to load organisations. Please try again.')
				}
			} finally {
				if (!cancelled) setLoadingOptions(false)
			}
		}
		run()
		return () => {
			cancelled = true
		}
	})

	useEffect(() => {
		loadOptions()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.id, api])

	const optionsReady = !!user && loadedUserId === user.id && loadedApi === api && !loadingOptions && !lookupError

	const close = () => {
		context.current.generation += 1
		setCompanyId('')
		setError(null)
		onClose()
	}

	const submit = async () => {
		if (!user || !optionsReady) {
			return
		}
		const generation = context.current.generation
		setSubmitting(true)
		setError(null)
		try {
			const csrf = await getCSRFToken()
			if (generation !== context.current.generation) return
			const body = await api.post(`admin/companies/${companyId}/users/${user.id}/attach`, {
				_csrf: csrf,
				roleId,
			})
			if (generation !== context.current.generation) return
			if (body.success !== true) {
				setError(getApiErrorMessage({ body }, 'Failed to add the user to the organisation. Please try again.'))
				return
			}
			close()
			onAdded()
		} catch (err) {
			if (generation === context.current.generation) {
				setError(getApiErrorMessage(err, 'Failed to add the user to the organisation. Please try again.'))
			}
		} finally {
			if (generation === context.current.generation) setSubmitting(false)
		}
	}

	return (
		<CanonicalModal
			open={user !== null}
			onClose={close}
			size='small'
			title='Add to organisation'
			subtitle={user ? `${user.fullName} (${user.email})` : undefined}
			actions={
				<div className='flex space-x-3'>
					<Button
						type='Primary'
						text='Add to organisation'
						disabled={submitting || !optionsReady || !companyId || !roleId}
						onClick={submit}
					/>
					<Button type='Outlined' text='Cancel' disabled={submitting} onClick={close} />
				</div>
			}
		>
			{loadingOptions && <LoadingSpinner inline loadingText='Loading organisations and roles...' />}
			{lookupError && (
				<div role='alert' className='text-error-500 text-sm mt-2'>
					<p>{lookupError}</p>
					<Button type='Primary' text='Retry organisation lookup' onClick={loadOptions} />
				</div>
			)}
			<div className='mt-6'>
				<Label className='block'>Organisation</Label>
				<SearchableSelect
					name='Organisation'
					options={optionsReady ? companyOptions : []}
					isDisabled={!optionsReady}
					isMulti={false}
					isSearchable
					isClearable={false}
					fillContainer
					value={companyOptions.find((option) => option.value === companyId) ?? null}
					onChange={(option) => setCompanyId(option?.value ?? '')}
				/>
			</div>
			<div className='mt-6'>
				<Label className='block'>Role</Label>
				<SearchableSelect
					name='Role'
					options={optionsReady ? roleOptions : []}
					isDisabled={!optionsReady}
					isMulti={false}
					isSearchable
					isClearable={false}
					fillContainer
					value={roleOptions.find((option) => option.value === roleId) ?? null}
					onChange={(option) => setRoleId(option?.value ?? '')}
				/>
			</div>
			{error && <div className='text-error-500 text-sm mt-2'>{error}</div>}
		</CanonicalModal>
	)
}

export default AddToOrganisationModal
